import { defineStore } from 'pinia'
import { useStorage } from '@vueuse/core'
import { adminRoles, MarketingNotificationsStatus, User } from '~/types/users'
import { router } from '~/router'
import { RouteLocationRaw } from 'vue-router'
import { Partner } from '~/types/partners'
import { userHasRole } from '~/middleware/helpers'
import { jwtDecode } from 'jwt-decode'
import {
  identifyAnalyticsUser,
  resetAnalyticsUser,
} from '~/composables/analytics'
import { StorageKey } from '~/constants/storage'
import { AccessTokenPayload } from '~/types/auth'

export const useUserStore = defineStore('user', () => {
  const refreshKey = ref(0)
  const api = useHttpClient()
  const doNotLogout = ref(false)

  const selectedPartnerId = useStorage(StorageKey.SELECTED_PARTNER_ID, '')
  const tokens = useStorage(StorageKey.TOKENS, { access: '', refresh: '' })

  /* User data */
  const defaultRoles: string[] = []
  const me = ref({
    id: '',
    email: '',
    roles: defaultRoles,
    firstName: '',
    lastName: '',
    marketingNotifications: MarketingNotificationsStatus.PENDING,
    marketingNotificationsUpdatedAt: null as string | null,
  })
  let analyticsUserId = ''
  const adminId = ref('')
  const partner = ref({} as Partner)

  function decodeAccessToken(accessToken?: string) {
    const token = accessToken ?? tokens.value.access
    if (!token.length) return undefined
    try {
      return jwtDecode<AccessTokenPayload>(token)
    } catch {
      return undefined
    }
  }

  function resetStore() {
    resetAnalyticsUser()
    analyticsUserId = ''
    me.value = {
      id: '',
      email: '',
      roles: defaultRoles,
      firstName: '',
      lastName: '',
      marketingNotifications: MarketingNotificationsStatus.PENDING,
      marketingNotificationsUpdatedAt: null,
    }
    adminId.value = ''
    partner.value = { id: '', name: '' }
    // .replace error because of this line
    tokens.value = { access: '', refresh: '' }
    refreshKey.value++
    selectedPartnerId.value = ''
  }

  async function changePartner(id: string) {
    selectedPartnerId.value = id
    await fetchPartner()
  }

  function changeDoNotLogout(value: boolean) {
    doNotLogout.value = value
  }

  // @ts-ignore
  const partnerId = computed(() => {
    if (selectedPartnerId.value.length > 0) return selectedPartnerId.value
    if (userHasRole(adminRoles)) return ''

    const decoded = decodeAccessToken()
    const partnerRoles = decoded?.partner_roles ?? []

    if (!partnerRoles.length && me.value.id) {
      logout({ name: 'subscription-portal-sign-in' })
      return ''
    }

    return partnerRoles.length
      ? partnerRoles[partnerRoles.length - 1].partnerId
      : ''
  })

  const allPartnerIDs = computed(() => {
    const decoded = decodeAccessToken()
    const roles = decoded?.partner_roles ?? []
    return roles?.length ? roles.map((obj) => obj.partnerId) : ''
  })

  async function fetchPartner() {
    if (!partnerId.value) return
    const partnerResponse = await api.partners.getSingle(partnerId.value)
    partner.value = partnerResponse
    return partnerResponse
  }

  function getUserId() {
    return decodeAccessToken()?.user_id ?? undefined
  }
  function setMyData(user: User) {
    const stableUserId = getUserId()

    if (analyticsUserId && stableUserId && analyticsUserId !== stableUserId) {
      resetAnalyticsUser()
      analyticsUserId = ''
    }

    me.value.id = user.id
    me.value.email = user.email
    me.value.firstName = user.firstName ?? ''
    me.value.lastName = user.lastName ?? ''
    me.value.roles = user.roles?.length ? user.roles : defaultRoles
    me.value.marketingNotifications =
      user.marketingNotifications ?? MarketingNotificationsStatus.PENDING
    me.value.marketingNotificationsUpdatedAt =
      user.marketingNotificationsUpdatedAt ?? null

    if (stableUserId) {
      identifyAnalyticsUser({
        id: stableUserId,
        roles: me.value.roles,
      })
      analyticsUserId = stableUserId
    }
    checkForAdminId(tokens.value.access)
  }
  function setMarketingNotifications(
    status: MarketingNotificationsStatus,
    updatedAt: string,
  ) {
    me.value.marketingNotifications = status
    me.value.marketingNotificationsUpdatedAt = updatedAt
  }
  function setMyAttr(key: 'email' | 'firstName' | 'lastName', value: string) {
    me.value[key] = value
  }

  /* Logged-in */
  const isLoggedIn = computed(() => {
    return !!(getTokens.value.access && getTokens.value.refresh)
  })
  function logout(to: RouteLocationRaw | null) {
    try {
      resetStore()
    } catch {
      localStorage.clear()
    } finally {
      if (to) router.push(to)
    }
  }

  /* Tokens */
  const getTokens = computed(() => {
    return { access: tokens.value.access, refresh: tokens.value.refresh }
  })
  function setTokens(accessToken: string, refreshToken: string) {
    if (!accessToken || !refreshToken) return
    tokens.value.access = accessToken
    tokens.value.refresh = refreshToken
  }

  function checkForAdminId(accessToken: string) {
    if (!accessToken) {
      adminId.value = ''
      return
    }
    adminId.value = decodeAccessToken(accessToken)?.admin_id ?? ''
  }

  async function stopImpersonating() {
    const { setup } = useWorkspace()
    if (decodeAccessToken()?.admin_id) {
      const { accessToken, refreshToken } = await api.auth.stopImpersonating()
      await setup(accessToken, refreshToken)
    }
    selectedPartnerId.value = ''
    adminId.value = ''
  }

  return {
    adminId,
    isLoggedIn,
    getTokens,
    setTokens,
    checkForAdminId,
    stopImpersonating,
    me,
    partner,
    partnerId,
    allPartnerIDs,
    fetchPartner,
    changeDoNotLogout,
    doNotLogout,
    setMyData,
    setMyAttr,
    setMarketingNotifications,
    getUserId,
    resetStore,
    logout,
    changePartner,
  }
})
